import { useId, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { cn } from '../../../lib/cn';

/**
 * An audience's title: its identity once filled in (spec:267), else "New audience" in the wizard or
 * "Audience {n}" on the Edit page (`index` counts from 0).
 */
export function audienceTitle(identity: string | null | undefined, mode: 'edit' | 'read', index: number): string {
  const name = identity?.trim();
  if (name) return name;
  return mode === 'edit' ? 'New audience' : `Audience ${index + 1}`;
}

/**
 * One audience as a collapsible card (spec:267, :175): a heading button that opens and closes it, and
 * the fields inside. In the wizard (`mode="edit"`) an open card ends with a plain "Remove audience"
 * button; the Edit page (`mode="read"`) has none, and no Add.
 */
export function AudienceCard({
  mode,
  title,
  expanded,
  onToggle,
  onRemove,
  removeDisabled = false,
  children,
}: {
  mode: 'edit' | 'read';
  title: string;
  expanded: boolean;
  onToggle: () => void;
  /** Wizard only: takes the card out of the draft (deleted when step 7 is saved). */
  onRemove?: () => void;
  removeDisabled?: boolean;
  children: ReactNode;
}) {
  const bodyId = useId();
  return (
    <section className="rounded-xl border border-line bg-card">
      <h3 className="text-sm font-semibold text-ink">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={onToggle}
          className={cn(
            'focus-ring flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-hover',
            expanded ? 'rounded-t-xl' : 'rounded-xl',
          )}
        >
          <span className="min-w-0 break-words">{title}</span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-ink-muted transition-transform motion-reduce:transition-none', expanded && 'rotate-180')}
            aria-hidden
          />
        </button>
      </h3>
      <div id={bodyId} hidden={!expanded} className="space-y-6 border-t border-line-soft px-4 py-4">
        {children}
        {mode === 'edit' && onRemove && (
          <div>
            <Button
              type="button"
              variant="plain"
              size="sm"
              disabled={removeDisabled}
              onClick={onRemove}
              aria-label={`Remove audience: ${title}`}
            >
              Remove audience
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
